import PT from 'prop-types';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { selectWidths } from '~/common/constants/selectWidths';
import { getWorkflowConstants } from '~/common/constants/workflow';
import FieldAssignmentMenu from '~/components/Workflow/WorkflowBuilderPage/Sidebar/FieldAssignmentMenu/FieldAssignmentMenu';
import { Select } from '~/eds/src/components/Select/Select';
import { useIntl } from 'react-intl';
import { getFieldOptions } from '~/reducers/workflow';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import InputLabel from '~/ui/components/InputLabel/InputLabel';
import Text from '~/ui/components/Text/Text';

function FieldSelect({
  blacklistValues,
  enableAddField = false,
  id,
  value,
  placeholder = '...',
  shouldShowLabel,
  width,
  onChange,
  // connected
  fields,
  options,
  shouldValidate,
}) {
  const [isAddingField, setIsAddingField] = useState(false);
  const [newFieldData, setNewFieldData] = useState();
  const intl = useIntl();
  const { fieldTypeLabels } = getWorkflowConstants(intl);

  useEffect(
    () => {
      if (newFieldData) {
        onChange(newFieldData.fieldId);
      }
    },
    [newFieldData], // eslint-disable-line react-hooks/exhaustive-deps
  );

  let error;
  if (shouldValidate) {
    if (value == null) {
      error = intl.formatMessage({
        id: 'FieldSelect.missingField',
        defaultMessage: 'Missing Field',
        description: 'Error message when no field is selected',
      });
    } else if (!options.find((option) => option.value === value)) {
      error = intl.formatMessage({
        id: 'FieldSelect.invalidField',
        defaultMessage: 'Invalid Field',
        description: 'Error message when selected field is invalid',
      });
    }
  }

  function menuHeaderRenderer() {
    return (
      <Box px={3} onClick={() => setIsAddingField(true)}>
        <Text color="gray-900" variant="s-dense">
          {intl.formatMessage({
            id: 'FieldSelect.addNewField',
            defaultMessage: 'Add new field',
            description: 'Menu option to add a new field',
          })}
        </Text>
      </Box>
    );
  }

  const input = (
    <Select
      isClearable={false}
      error={error}
      id={id}
      options={options}
      placeholder={error || placeholder}
      value={value}
      width={selectWidths[width]}
      menuHeader={enableAddField ? menuHeaderRenderer() : undefined}
      onChange={onChange}
      filterOption={(option) => !blacklistValues.includes(option.value)}
      enablePortal={true}
    />
  );

  if (shouldShowLabel) {
    return (
      <FlexLayout sx={{ alignSelf: 'flex-start' }}>
        <InputLabel
          id="field-select"
          isRequired
          label={intl.formatMessage({
            id: 'FieldSelect.fieldLabel',
            defaultMessage: 'Field',
            description: 'Label for field selection input',
          })}
          labelRightContent={
            <Text color="gray-600" variant="2xs-dense-caps">
              {fieldTypeLabels[fields[value]?.type]}
            </Text>
          }
        >
          {input}
        </InputLabel>
        {isAddingField && (
          <FieldAssignmentMenu
            isVisible={isAddingField}
            trigger={<div />}
            onFieldAssign={(fieldAssignData) => {
              setNewFieldData(fieldAssignData);
              setIsAddingField(false);
            }}
            onHide={() => setIsAddingField(false)}
          />
        )}
      </FlexLayout>
    );
  }

  return input;
}

FieldSelect.propTypes = {
  blacklistValues: PT.arrayOf(PT.string.isRequired),
  enableAddField: PT.bool,
  value: PT.string,
  onChange: PT.func.isRequired,
  placeholder: PT.string,
};

const mapStateToProps = ({ builder, workflow }, { enableImplicitFields }) => ({
  fields: workflow.fields,
  options: getFieldOptions(workflow, enableImplicitFields),
  shouldValidate: builder.shouldValidate,
});

export default connect(mapStateToProps)(FieldSelect);
